import {
  PointLight,
  Color,
  SphereGeometry,
  ShaderMaterial,
  Mesh,
  DoubleSide,
  AdditiveBlending,
} from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import {
  Lensflare,
  LensflareElement,
} from "three/examples/jsm/objects/Lensflare.js";

import { Belt } from "../groupsEntities/asteroidsBelt.js";
import { Kuiper } from "../groupsEntities/asteroidsKuiper.js";
import { CloudOort } from "../groupsEntities/cloudOort.js";

import { createMaterial } from "./materialFactory.js";
import { setLabelVisible } from "../helpers.js";

import { createOrbitCircle, orbitWarp } from "../utils/orbitUtils.js";

import { cameraLabelClick } from "../utils/cameraUtils.js";

import { showInfoPanel } from "../ui.js";

import sunTextureFlares from "../../../assets/scenario/lensflare/sunFlare.png";
import fragmentAround from "../../../shaders/atmAround/fragment.glsl";
import vertexAround from "../../../shaders/atmAround/vertex.glsl";

import { sceneManager} from "../script";
import { bodies } from "../configEntities/celestialDynamicsConfig.js";

import { initMagneticField, updateMagnetic } from "../utils/magneticTangled.js";

import { normalizeAlphabetic } from "../helpers.js";
import { INCLIN_GALAXY, WAVE_SPEED } from "../configEntities/galaxyConfig.js";
import { loading } from "../loading/loading.js";

export class Star {
  constructor(
    size,
    orbitRadius,
    axialTilt,
    orbitInclin,
    speedTrans,
    speedRot,
    eccentricity,
    name,
    centerElement
  ) {
    this.orbitRadius = orbitRadius;
    this.axialTilt = axialTilt;
    this.orbitInclin = orbitInclin;
    this.speedTrans = speedTrans;
    this.eccentricity = eccentricity;
    this.size = size;
    this.name=name

    this.argPeriapsis = 0;
    this.longAscNode = 0;

    let label = normalizeAlphabetic(name);
    
    this.time = 0
    this.precessionPhase = 0
    this.warpPhase = 0
    this.objOsc = bodies[label];
    this.distScale = this.objOsc.distScale;
    this.distRot = this.objOsc.distRot;
    this.ia = this.objOsc.ia;
    this.ifreq = this.objOsc.ifreq;
    this.ra = this.objOsc.ra;
    this.rfreq = this.objOsc.rfreq;
    this.precession = this.objOsc.precession;

    this.geo = new SphereGeometry(this.size, 64, 64);
    this.speedRot = speedRot;
    this.mat = createMaterial(name, null, null, this);

    this.mesh = new Mesh(this.geo, this.mat);
    this.mesh.position.set(0, 0, 0);
    this.inclinaRad = (this.axialTilt * Math.PI) / 180.0;
    this.mesh.rotation.x = this.inclinaRad;
  
    this.orbit = createOrbitCircle(
      this.orbitRadius,
      this.orbitInclin,
      this.ia,
      this.ifreq,
      this.ra,
      this.rfreq,
      this.argPeriapsis,
      this.eccentricity
    );
    this.originalPositions =
      this.orbit.geometry.attributes.position.array.slice();
    this.orbit.rotation.x = INCLIN_GALAXY;

    sceneManager.scene.add(this.orbit);
    sceneManager.scene.add(this.mesh);

    this.centerElement = centerElement;
    this.initElements(sceneManager.scene);

    this.div = document.createElement("div");
    this.div.className = "label";
    this.div.textContent = name;
    this.div.style.backgroundColor = "transparent";
    this.div.style.cursor = "pointer";
    this.infoIcon = document.createElement("i");
    this.infoIcon.className = "fas fa-info-circle info-icon";
    this.infoIcon.style.pointerEvents = "auto";
    this.infoIcon.title = "More Information";
    this.div.appendChild(this.infoIcon);
    this.div.addEventListener("click", () => {
      cameraLabelClick(name, this);
    });

    this.infoIcon.addEventListener("click", (evt) => {
      evt.stopPropagation();
      showInfoPanel(name);
    });

    const canvas = sceneManager.renderer.domElement;
    this.div.addEventListener('wheel', e => {
      e.preventDefault();        
      const evt = new WheelEvent('wheel', e);
      canvas.dispatchEvent(evt);    
    });

    this.label = new CSS2DObject(this.div);
    sceneManager.scene.add(this.label);
  }

  initElements(scene) {
    this.pointLight = new PointLight(0xffffff, 2.5, 1000, 1e-2);
    scene.add(this.pointLight);
    this.matAround = new ShaderMaterial({
      transparent: true,
      uniforms: {
        u_color:           { value: new Color(0xF7F8C8) },
      },
      vertexShader: vertexAround,
      fragmentShader: fragmentAround,
      blending: AdditiveBlending,
      depthTest: true,
      depthWrite: false,
      side: DoubleSide,
    });
    this.geoAround = new SphereGeometry(this.size * 1.6, 32, 32);
    this.meshAround = new Mesh(this.geoAround, this.matAround);
    scene.add(this.meshAround);
    this.mesh.add(this.meshAround);

    this.textureLoaderFlare = loading.textureLoader
    this.textureFlare1 = this.textureLoaderFlare.load(sunTextureFlares);

    this.lensFlare = new Lensflare();
    this.flareElements = [];
    this.mainFlare = new LensflareElement(
      this.textureFlare1,
      500,
      0.0,
      new Color(0xffffff)
    );
    this.lensFlare.addElement(this.mainFlare);
    scene.add(this.lensFlare);
    this.fieldMagnetic = initMagneticField(this);

    this.asteroids = [
     new Belt(this),
     new Kuiper(this),
     new CloudOort(this)
    ]
  }

 updateAsteroids(speed) {
  this.asteroids.forEach(group => group.animate(speed));
}
  animate(speed) {
    sceneManager.cubeCamera1.update(
      sceneManager.renderer,
      sceneManager.sceneCube
    );

   this.time = ((this.time+speed*this.speedTrans)% (2 * Math.PI))
   
   this.precessionPhase =
      (this.precessionPhase + speed * this.speedTrans * this.precession) % (2 * Math.PI);


  this.warpPhase = (this.warpPhase + speed * WAVE_SPEED) % (2 * Math.PI);

    this.mat.uniforms.uPerlin.value = sceneManager.cubeRenderTarget1.texture;
    this.mat.uniforms.time.value += 1e-3;
    updateMagnetic(this.fieldMagnetic, speed);
    this.label.visible = setLabelVisible(
      this.mesh,
      this.size,
      this.orbitRadius,
      this.label,
      sceneManager.camera,
      sceneManager.commands.controls.labels
    );

    orbitWarp(
      this.orbit,
      this.time,
      this.objOsc.warp,
      this.precessionPhase,
      this.warpPhase,
      this.longAscNode
    );

    this.mesh.rotateY(speed* this.speedRot);
    this.orbit.geometry.attributes.position.needsUpdate = true;

    this.center = this.centerElement.mesh.position;
    this.orbit.position.copy(this.centerElement.mesh.position);
    this.orbit.visible = sceneManager.commands.controls.orbit;

    this.orbit.material.uniforms.uInvertAlpha.value = sceneManager.dimension;


    const camDist = sceneManager.camera.position.length();

    const sizeFlare = 160000 / camDist;
    if (this.mainFlare)
      this.mainFlare.size = Math.min(Math.max(0.01, sizeFlare), 360);

    this.updateAsteroids(speed)
  }

  centerOrbit() {
    this.center = this.centerElement.mesh.position;
    this.orbit.position.copy(this.centerElement.mesh.position);
  }
}
